import React, { useState, useEffect, useRef } from 'react';
import { Parser } from 'acorn';
import jsx from 'acorn-jsx';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import JsxParser from 'react-jsx-parser';

const API_BASE = 'https://api.insightos.co.uk';
const MAX_GENERATION_RETRIES = 2;
const JsxSyntaxParser = Parser.extend(jsx());

export const ProcessDocBuilder = ({ info, name, role }) => {
  const [processData, setProcessData] = useState([]);
  const [currentPage, setCurrentPage] = useState(null);
  const [processName, setProcessName] = useState('');
  const [queue, setQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [existingPages, setExistingPages] = useState([]);
  const [preview, setPreview] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [renderError, setRenderError] = useState(null);
  const [generationError, setGenerationError] = useState(null);
  const [retryCounts, setRetryCounts] = useState({});
  const [isVideoPreview, setIsVideoPreview] = useState(false);
  const [jobStage, setJobStage] = useState('');
  const [jobProgress, setJobProgress] = useState(0);
  const [jobLogs, setJobLogs] = useState([]);
  const isMountedRef = useRef(true);
  const navigate = useNavigate();

  const sanitizeJsx = (input) =>
    (input || '')
      .replace(/^```(?:jsx|javascript|js)?\s*/i, '')
      .replace(/\s*```$/i, '')
      .replace(/\{\/\*[^]*?\*\/\}/g, '')
      .replace(/\{\s*\}/g, '');

  const appendLocalJobLog = (message) => {
    setJobLogs((currentLogs) => {
      const nextLogs = [...currentLogs, message];
      return nextLogs.slice(-6);
    });
  };

  const validatePreviewJsx = (input) => {
    const normalizedJsx = sanitizeJsx(input).trim();

    if (!normalizedJsx) {
      return {
        isValid: false,
        message: 'Generated JSX was empty after sanitization.',
      };
    }

    try {
      JsxSyntaxParser.parse(`const __preview = (${normalizedJsx});`, {
        ecmaVersion: 'latest',
        sourceType: 'module',
      });

      return {
        isValid: true,
        jsx: normalizedJsx,
      };
    } catch (error) {
      return {
        isValid: false,
        message: error?.message || 'Generated JSX failed syntax validation.',
      };
    }
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchProcess = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/getsteps/${info}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();
        if (!data?.data?.length) return;
        setProcessName(data.data[0].ProcessName);
        setProcessData(data.data);
        setExistingPages(data.trainingPages || []);
      } catch (error) {
        console.error(error);
      }
    };

    fetchProcess();
  }, [info]);


  useEffect(() => {
    if (processData.length === 0) return;

    let pageNumber = 1;
    const items = [];

    for (const task of processData) {
      if (!task.Task?.trim() || !task.Description?.trim()) {
        console.warn(`⚠️ Skipping empty task`, task);
        continue;
      }

      items.push({
        Title: task.Task,
        TrainingDocName: processName,
        Code: code2,
        Description: task.Description,
        Page: pageNumber,
        Type: "Task",
        ProcessId: info,
        Role: role
      });
      pageNumber++;

      if (Array.isArray(task.subSteps) && task.subSteps.length > 0) {
        for (const subStep of task.subSteps) {
          if (!subStep.Name?.trim() || !subStep.Description?.trim()) {
            console.warn(`⚠️ Skipping empty substep`, subStep);
            continue;
          }

          items.push({
            Title: subStep.Name,
            TrainingDocName: processName,
            Code: code1,
            Description: subStep.Description,
            Page: pageNumber,
            Type: "Substep",
            ProcessId: info,
            Role: role
          });
          pageNumber++;
        }
      }
    }

    // Resume from the first item whose title hasn't been generated yet
    const generatedTitles = new Set(existingPages.map(p => p.Title));
    const startIndex = existingPages.length > 0 ? items.findIndex(item => !generatedTitles.has(item.Title)) : 0;

    // Renumber remaining items to continue after the highest existing page (accounts for video pages)
    if (existingPages.length > 0 && startIndex > 0) {
      const maxExistingPage = Math.max(...existingPages.map(p => Number(p.Page)));
      let nextPage = maxExistingPage + 1;
      for (let i = startIndex; i < items.length; i++) {
        items[i] = { ...items[i], Page: nextPage++ };
      }
    }

    setQueue(items);
    setCurrentIndex(startIndex >= 0 ? startIndex : items.length);
    setPreview(null);
    setRenderError(null);
    setGenerationError(null);
    setIsVideoPreview(false);
    setJobStage('');
    setJobProgress(0);
    setJobLogs([]);
  }, [processData, processName, role, info, existingPages]);

  const deleteTrainingPage = async (pageId) => {
    if (!pageId) return;
    try {
      await fetch('https://api.insightos.co.uk/api/deletepage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pageId })
      });
    } catch (error) {
      console.error('Failed to delete page:', error);
    }
  };

  const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

  const waitForQueuedJob = async (jobId) => {
    for (let attempt = 0; attempt < 240; attempt += 1) {
      const response = await fetch(`${API_BASE}/api/redisjobs/${jobId}`, {
        method: 'GET',
        credentials: 'include',
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch queued training page status.');
      }

      if (isMountedRef.current) {
        const nextProgress = typeof data?.progress === 'object'
          ? Number(data.progress.percent || 0)
          : Number(data?.progress || 0);
        setJobProgress(nextProgress);
        setJobStage(data?.progress?.stage || data?.status || 'Queued');
        setJobLogs(Array.isArray(data?.logs) ? data.logs.slice(-6) : []);
      }

      if (data.status === 'completed') {
        return data.result || null;
      }

      if (data.status === 'failed') {
        throw new Error(data.failedReason || 'Queued training page generation failed.');
      }

      await sleep(1500);
    }

    throw new Error('Timed out waiting for queued training page generation.');
  };

  const generatePage = async (item) => {
    setIsGenerating(true);
    setRenderError(null);
    setGenerationError(null);
    setCurrentPage(item.Page);
    setJobStage('Queueing training page');
    setJobProgress(0);
    setJobLogs([]);

    try {
      for (let retryAttempt = 0; retryAttempt <= MAX_GENERATION_RETRIES; retryAttempt += 1) {
        const response = await fetch(`${API_BASE}/api/submitaidoc`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(item),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result?.message || `Failed to submit ${item.Title}`);
        }

        const pageResult = result?.queued && result?.jobId
          ? await waitForQueuedJob(result.jobId)
          : result;

        if (!pageResult?.trainingId || !pageResult?.code) {
          throw new Error('Invalid response for training page');
        }

        const validationResult = validatePreviewJsx(pageResult.code);
        if (validationResult.isValid) {
          setJobStage('Completed');
          setJobProgress(100);

          setPreview({
            ...item,
            trainingId: pageResult.trainingId,
            code: validationResult.jsx,
            content: pageResult.content || '',
            kind: 'page'
          });
          return;
        }

        await deleteTrainingPage(pageResult.trainingId);

        const attemptLabel = retryAttempt + 1;
        appendLocalJobLog(`[validation] Invalid JSX detected. Deleted page ${pageResult.trainingId}.`);

        if (retryAttempt === MAX_GENERATION_RETRIES) {
          throw new Error(`Generated JSX failed validation after ${attemptLabel} attempts: ${validationResult.message}`);
        }

        setJobStage(`Generated page failed validation. Regenerating (${attemptLabel}/${MAX_GENERATION_RETRIES + 1})`);
        setJobProgress(0);
      }
    } catch (error) {
      console.error('🚨 Error sending data:', error);
      setGenerationError(error?.message || 'Failed to generate training page.');
    } finally {
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    if (queue.length === 0) return;
    if (preview || isGenerating || generationError) return;
    if (currentIndex >= queue.length) {
      // Mark training as finished then navigate
      fetch(`${API_BASE}/api/training/mark-finished`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ processId: info }),
      }).catch(console.error).finally(() => {
        navigate('/training');
      });
      return;
    }
    generatePage(queue[currentIndex]);
  }, [queue, currentIndex, preview, isGenerating, generationError, navigate]);

  const createVideoPage = async ({ baseTrainingId, videoPage }) => {
    setIsGenerating(true);
    setRenderError(null);

    try {
      const response = await fetch('https://api.insightos.co.uk/api/youtubesearch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          TrainingId: baseTrainingId,
          Code: code3,
          Page: videoPage,
          TrainingDocName: preview?.TrainingDocName,
          ProcessId: preview?.ProcessId,
          Role: preview?.Role
        })
      });

      if (!response.ok) {
        console.error('❌ Failed to add video page');
        return false;
      }

      const result = await response.json();
      if (!result?.trainingId || !result?.code) {
        console.error('❌ Invalid response for video page');
        return false;
      }

      setPreview({
        TrainingDocName: preview?.TrainingDocName,
        ProcessId: preview?.ProcessId,
        Role: preview?.Role,
        Title: result.title || `${preview?.Title || 'Training'} - Video`,
        Description: result.description || '',
        Page: videoPage,
        Type: 'Video',
        baseTrainingId,
        trainingId: result.trainingId,
        code: result.code,
        content: result.content || '',
        kind: 'video',
        videoMeta: result.video || null
      });
      setIsVideoPreview(true);
      return true;
    } catch (error) {
      console.error('🚨 Error adding video:', error);
      return false;
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRetry = async () => {
    if (!preview) return;

    if (isVideoPreview) {
      await deleteTrainingPage(preview.trainingId);
      await createVideoPage({
        baseTrainingId: preview.baseTrainingId,
        videoPage: preview.Page
      });
      return;
    }

    const retryKey = preview.trainingId || `${currentIndex}`;
    const retryCount = retryCounts[retryKey] || 0;
    if (retryCount >= 2) {
      setRenderError(new Error('Retry limit reached.'));
      return;
    }

    setRetryCounts((prev) => ({ ...prev, [retryKey]: retryCount + 1 }));
    await deleteTrainingPage(preview.trainingId);
    setPreview(null);
    setRenderError(null);
  };

  const handleNext = () => {
    setPreview(null);
    setRenderError(null);
    setGenerationError(null);
    setIsVideoPreview(false);
    setJobStage('');
    setJobProgress(0);
    setJobLogs([]);
    setCurrentIndex((prev) => prev + 1);
  };

  const handleRetryGeneration = () => {
    setGenerationError(null);
    setRenderError(null);
    setPreview(null);
    setJobStage('');
    setJobProgress(0);
    setJobLogs([]);
  };

  const handleRenderError = async (error) => {
    console.error('Render error:', error);
    setRenderError(error);
    await handleRetry();
  };

  const insertVideoIntoQueue = (basePage) => {
    setQueue((prev) => {
      const updated = prev.map((item, index) => {
        if (index > currentIndex) {
          return { ...item, Page: item.Page + 1 };
        }
        return item;
      });
      return updated;
    });
    return basePage + 1;
  };

  const removeVideoFromQueue = () => {
    setQueue((prev) =>
      prev.map((item, index) =>
        index > currentIndex ? { ...item, Page: item.Page - 1 } : item
      )
    );
  };

  const handleAddVideo = async () => {
    if (!preview) return;
    const videoPage = insertVideoIntoQueue(preview.Page);
    const created = await createVideoPage({
      baseTrainingId: preview.trainingId,
      videoPage
    });

    if (!created) {
      removeVideoFromQueue();
    }
  };

  const handleDeleteVideo = async () => {
    if (!preview) return;
    await deleteTrainingPage(preview.trainingId);
    removeVideoFromQueue();
    setPreview(null);
    setIsVideoPreview(false);
    setRenderError(null);
    setCurrentIndex((prev) => prev + 1);
  };

  const handleFinish = () => {
    navigate('/training');
  };


  const code1 = `
  <div className="relative w-full max-w-4xl mx-auto overflow-hidden rounded-2xl shadow-xl bg-white border border-gray-100">
    {/* Course Lesson Header */}
    <div className="flex items-center justify-between px-5 sm:px-7 md:px-10 py-4 sm:py-5 bg-gradient-to-r from-indigo-600 to-purple-600">
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-white/15 text-white text-xs sm:text-sm font-bold">L</div>
        <div className="text-white">
          <div className="text-[10px] sm:text-xs font-semibold tracking-widest uppercase">Lesson</div>
          <div className="text-sm sm:text-base font-semibold">Step Guide</div>
        </div>
      </div>
      <div className="hidden sm:flex items-center gap-2 text-white/90 text-xs">
        <span className="px-2 py-1 rounded-full bg-white/15">Quick Read</span>
        <span className="px-2 py-1 rounded-full bg-white/15">Practical</span>
      </div>
    </div>

    {/* Content Section */}
    <div className="flex flex-col justify-center px-5 sm:px-7 md:px-10 lg:px-12 py-6 sm:py-8 md:py-10 bg-gradient-to-br from-slate-50 to-white">
      <div className="space-y-3 sm:space-y-4">
        <h2 className="text-base sm:text-lg md:text-2xl lg:text-3xl font-bold leading-snug \${headingColor} animate-slide-in" 
            style={{ fontFamily: \${font} }}>
          \${heading}
        </h2>
        <div className="w-12 sm:w-16 h-1 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full"></div>
        <div className="flex flex-wrap items-center gap-2 text-[11px] sm:text-xs text-slate-600">
          <span className="px-2 py-1 rounded-full bg-slate-100">Objective</span>
          <span className="px-2 py-1 rounded-full bg-slate-100">Step-by-step</span>
          <span className="px-2 py-1 rounded-full bg-slate-100">Checklist</span>
        </div>
        <p className="text-xs sm:text-sm md:text-base lg:text-lg leading-relaxed \${paragraphColor} animate-slide-in" 
           style={{ fontFamily: \${font} }}>
          \${paragraph}
        </p>
      </div>
    </div>
  </div>
  `;

  
const code2 = `
  <div className="\${bgColor} py-10 sm:py-14 md:py-16 relative overflow-hidden">
    {/* Background Decorative Elements */}
    <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-purple-50 opacity-60"></div>
    <div className="absolute top-0 right-0 w-72 h-72 sm:w-96 sm:h-96 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
    <div className="absolute bottom-0 left-0 w-72 h-72 sm:w-96 sm:h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
    
    <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
      {/* Course Header */}
      <div className="text-center mb-8 sm:mb-10 animate-slide-in">
        <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 shadow-lg mb-4 sm:mb-5">
          <img alt="Company Logo" src="2.png" className="w-8 h-8 sm:w-10 sm:h-10 object-contain brightness-0 invert" />
        </div>
        <p className="text-[11px] sm:text-xs font-semibold tracking-widest uppercase text-indigo-600 mb-2 \${headingColor}">
          \${doc}
        </p>
        <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight \${headingColor} animate-slide-in">
          \${heading}
        </h3>
        <div className="flex justify-center mt-3">
          <div className="w-20 sm:w-24 h-1 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-full"></div>
        </div>
        <div className="mt-4 flex flex-wrap justify-center gap-2 text-[11px] sm:text-xs text-slate-600">
          <span className="px-2 py-1 rounded-full bg-white/70 border border-slate-200">Module Overview</span>
          <span className="px-2 py-1 rounded-full bg-white/70 border border-slate-200">Key Concepts</span>
          <span className="px-2 py-1 rounded-full bg-white/70 border border-slate-200">Best Practices</span>
        </div>
      </div>

      {/* Content Card */}
      <div className="space-y-6 sm:space-y-8 animate-slide-in">
        <div className="relative bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300">
          {/* Accent Bar */}
          <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-indigo-600 to-purple-600"></div>
          
          <div className="p-5 sm:p-7 md:p-9">
            <div className="prose max-w-none">
              <p className="text-xs sm:text-sm md:text-base lg:text-lg leading-relaxed text-gray-700 \${paragraphColor}" 
                 style={{ fontFamily: \${font} }}>
                \${paragraph}
              </p>
            </div>
          </div>
          
          {/* Bottom Accent */}
          <div className="h-2 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600"></div>
        </div>
      </div>
    </div>
  </div>
`;

  const code3 = `
  <div className="relative w-full mx-auto rounded-2xl overflow-hidden border border-gray-200 bg-white shadow-xl">
    <div className="flex items-center justify-between px-5 sm:px-7 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
      <div>
        <div className="text-[10px] sm:text-xs uppercase tracking-widest text-white/80">Training Video</div>
        <div className="text-sm sm:text-base font-semibold">Watch and Learn</div>
      </div>
      <span className="text-xs sm:text-sm bg-white/20 px-2 py-1 rounded-full">Suggested</span>
    </div>
    <div className="p-4 sm:p-6">
      <div className="aspect-video w-full overflow-hidden rounded-xl bg-black">
        <iframe
          className="w-full h-full"
          src="__VIDEO_URL__"
          title="Training Video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        ></iframe>
      </div>
      <p className="mt-4 text-xs sm:text-sm text-gray-600">Tip: Watch the video and continue when you're ready.</p>
    </div>
  </div>
  `;



  return  (
    <div className="flex items-center justify-center min-h-screen bg-cover bg-center relative overflow-hidden" style={{ backgroundImage: "url('background.png')" }}>
      {/* Animated Blue Circles */}
      <motion.div
        className="absolute w-8 h-8 bg-blue-400 rounded-full opacity-70"
        animate={{
          x: [0, 200, -100, 150, 0],
          y: [0, -150, 100, -50, 0],
          scale: [1, 1.2, 0.8, 1.5, 1],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        style={{ top: '20%', left: '10%' }}
      />
      
      <motion.div
        className="absolute w-6 h-6 bg-blue-500 rounded-full opacity-60"
        animate={{
          x: [0, -180, 120, -80, 0],
          y: [0, 120, -80, 160, 0],
          scale: [1, 0.7, 1.3, 0.9, 1],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1
        }}
        style={{ top: '60%', right: '15%' }}
      />

      <motion.div
        className="absolute w-10 h-10 bg-blue-300 rounded-full opacity-50"
        animate={{
          x: [0, 100, -200, 50, 0],
          y: [0, -100, 80, -120, 0],
          scale: [1, 1.1, 0.6, 1.4, 1],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2
        }}
        style={{ bottom: '20%', left: '20%' }}
      />

      {/* Animated Purple Circles */}
      <motion.div
        className="absolute w-7 h-7 bg-purple-400 rounded-full opacity-70"
        animate={{
          x: [0, -150, 80, -120, 0],
          y: [0, 100, -140, 60, 0],
          scale: [1, 1.3, 0.8, 1.1, 1],
        }}
        transition={{
          duration: 9,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.5
        }}
        style={{ top: '40%', right: '20%' }}
      />

      <motion.div
        className="absolute w-9 h-9 bg-purple-500 rounded-full opacity-60"
        animate={{
          x: [0, 160, -90, 200, 0],
          y: [0, -80, 120, -160, 0],
          scale: [1, 0.9, 1.2, 0.7, 1],
        }}
        transition={{
          duration: 11,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1.5
        }}
        style={{ bottom: '30%', right: '10%' }}
      />

      <motion.div
        className="absolute w-5 h-5 bg-purple-300 rounded-full opacity-80"
        animate={{
          x: [0, -120, 160, -80, 0],
          y: [0, -60, 90, -110, 0],
          scale: [1, 1.4, 0.6, 1.2, 1],
        }}
        transition={{
          duration: 7,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 3
        }}
        style={{ top: '10%', right: '30%' }}
      />

      {/* Additional smaller circles for more dynamic effect */}
      <motion.div
        className="absolute w-4 h-4 bg-blue-200 rounded-full opacity-40"
        animate={{
          x: [0, 80, -60, 100, 0],
          y: [0, -40, 70, -90, 0],
          scale: [1, 0.8, 1.1, 0.9, 1],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2.5
        }}
        style={{ bottom: '10%', left: '40%' }}
      />

      <motion.div
        className="absolute w-3 h-3 bg-purple-200 rounded-full opacity-50"
        animate={{
          x: [0, -70, 90, -40, 0],
          y: [0, 50, -80, 110, 0],
          scale: [1, 1.2, 0.7, 1.3, 1],
        }}
        transition={{
          duration: 8.5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1.8
        }}
        style={{ top: '70%', left: '60%' }}
      />

      {/* Main loading / preview content */}
      {!preview ? (
        <motion.div 
          className="relative flex items-center justify-center text-blue-800 text-4xl font-semibold z-10"
          animate={{ 
            scale: [1, 1.05, 1],
            opacity: [0.8, 1, 0.8]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <div className="w-full max-w-2xl rounded-3xl border border-white/40 bg-white/85 p-6 shadow-2xl backdrop-blur">
            <div className="text-center text-blue-800 text-4xl font-semibold">
              {currentPage ? <span>Page {currentPage}</span> : <span>Preparing...</span>}
            </div>

            {generationError ? (
              <div className="mt-5 text-center">
                <p className="text-sm font-medium text-red-600">{generationError}</p>
                <button
                  type="button"
                  onClick={handleRetryGeneration}
                  className="mt-4 rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-600"
                >
                  Retry page generation
                </button>
              </div>
            ) : (
              <>
                {jobStage ? <p className="mt-4 text-center text-sm text-slate-700">{jobStage}</p> : null}
                <div className="mt-4 h-3 overflow-hidden rounded-full bg-slate-200">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400 transition-all duration-500"
                    style={{ width: `${Math.max(0, Math.min(100, jobProgress || 0))}%` }}
                  />
                </div>
                <p className="mt-2 text-center text-xs uppercase tracking-[0.2em] text-slate-500">
                  {Math.round(jobProgress || 0)}%
                </p>
                {jobLogs.length > 0 ? (
                  <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-950/95 p-3 text-left">
                    <p className="mb-2 text-[11px] uppercase tracking-[0.2em] text-slate-400">Job Log</p>
                    <div className="space-y-1 font-mono text-xs text-slate-200">
                      {jobLogs.map((logLine, index) => (
                        <p key={`${index}-${logLine}`} className="break-words">{logLine}</p>
                      ))}
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </motion.div>
      ) : (
        <div className="relative z-10 w-full max-w-5xl px-4 sm:px-6">
          <div className="bg-white/90 backdrop-blur rounded-2xl shadow-2xl border border-white/40 overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 sm:px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
              <div>
                <div className="text-xs uppercase tracking-widest text-white/80">Preview</div>
                <div className="text-lg font-semibold">
                  {preview.Title || 'Training Page'}
                </div>
              </div>
              <div className="text-xs sm:text-sm text-white/90">Page {preview.Page}</div>
            </div>

            <div className="p-4 sm:p-6 bg-white">
              <div className="prose prose-sm sm:prose-base lg:prose-lg max-w-none">
                <JsxParser
                  components={{}}
                  jsx={sanitizeJsx(preview.code)}
                  onError={handleRenderError}
                  onSuccess={() => setRenderError(null)}
                />
              </div>
            </div>

            {renderError && (
              <div className="px-5 sm:px-6 pb-4 text-sm text-red-600">
                Render error detected. Please retry.
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 px-5 sm:px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={handleFinish}
                className="w-full sm:w-auto px-4 py-2 rounded-lg bg-slate-800 text-white font-semibold hover:bg-slate-900 transition-colors"
                disabled={isGenerating}
              >
                Finished
              </button>
              <button
                onClick={handleRetry}
                className="w-full sm:w-auto px-4 py-2 rounded-lg bg-red-500 text-white font-semibold hover:bg-red-600 transition-colors"
                disabled={isGenerating}
              >
                Retry
              </button>
              <button
                onClick={handleNext}
                className="w-full sm:w-auto px-4 py-2 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-colors"
                disabled={isGenerating}
              >
                Next
              </button>
              {!isVideoPreview ? (
                <button
                  onClick={handleAddVideo}
                  className="w-full sm:w-auto px-4 py-2 rounded-lg bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2"
                  disabled={isGenerating}
                >
                  {isGenerating ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Searching Video...
                    </>
                  ) : (
                    'Add Video'
                  )}
                </button>
              ) : (
                <button
                  onClick={handleDeleteVideo}
                  className="w-full sm:w-auto px-4 py-2 rounded-lg bg-yellow-500 text-white font-semibold hover:bg-yellow-600 transition-colors"
                  disabled={isGenerating}
                >
                  Delete Video
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// JSX Code Templates
